import React, { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import NewNavbar from "../components/NewNavbar";
import Footer from "../components/Footer";
import HomeProfilePopupFlow from "../components/HomeProfilePopupFlow.js";
import AdvancedMeasurementsProModal from "../components/AdvancedMeasurementsProModal.js";
import {
  useDeleteDeactivateAccountMutation,
  useGetBodyTypesQuery,
  useGetFaceShapesQuery,
  useGetSeasonalColorsQuery,
  useProfileMeasurementsUpdateUpdateMutation,
  useProfileReadQuery,
  useProfileUpdateUpdateMutation,
} from "../store/api.ts";
import { convertImperialToMetric, convertMetricToImperial } from "../utils.js";
import SEO from "../components/SEO.js";

const hairColors = [
  "Black",
  "Dark Brown",
  "Light Brown",
  "Blonde",
  "Ash Blonde",
  "Platinum Blonde",
  "Red",
  "Auburn",
  "Ginger",
  "Chestnut",
  "Mahogany",
  "Burgundy",
  "Brunette",
  "Dirty Blonde",
  "Strawberry Blonde",
  "Silver",
  "Gray",
  "White",
  "Pastel Pink",
  "Pastel Blue",
  "Pastel Purple",
  "Pastel Green",
  "Pastel Yellow",
  "Bright Red",
  "Neon Blue",
  "Electric Purple",
  "Hot Pink",
  "Lime Green",
  "Turquoise",
  "Neon Orange",
  "Deep Tones",
  "Navy Blue",
  "Emerald Green",
  "Plum Purple",
  "Teal",
  "Metallic and Special Effects",
  "Metallic Silver",
  "Metallic Gold",
  "Rose Gold",
  "Bronze",
  "Multicolor and Techniques",
  "Rainbow",
  "Mermaid",
  "Unicorn",
  "Ombre",
  "Balayage",
];

const complexionTones = [
  "Fair",
  "Light",
  "Light to Medium",
  "Medium",
  "Medium to Dark",
  "Dark",
  "Very Dark",
];

const eyeColors = [
  "Light Blue",
  "Ice Blue",
  "Blue",
  "Grey-Blue",
  "White",
  "Black",
  "Grey",
  "Teal",
  "Ice Green",
  "Grey-Green",
  "Pale Gold",
  "Yellow",
  "Gold",
  "Orange",
  "Brown",
  "Dark Brown",
  "Red",
  "Pale Purple",
  "Purple",
  "Magenta",
  "Brown & Green",
  "Gold & Green",
  "Albino",
  "Blind",
  "Hazel",
  "Brownish Hazel",
  "Greenish Hazel",
  "Yellowish Hazel",
  "Amber",
  "Sapphire",
];

const skinTones = ["Cool", "Neutral", "Warm"];

const braBandSizes = [
  "28",
  "30",
  "32",
  "34",
  "36",
  "38",
  "40",
  "42",
  "44",
  "46",
  "48",
  "50",
  "52",
];

const braCupSizes = [
  "AA",
  "A",
  "B",
  "C",
  "D",
  "DD",
  "DDD",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
];

const sizes = ["XS", "S", "M", "L", "XL", "XXL"];

const emptyToNull = (value) => {
  if (value === undefined || value === null) return null;
  return String(value).trim() === "" ? null : value;
};

const labelClass =
  "mb-[8px] block font-spaceMono text-[10px] font-bold uppercase tracking-[0.28em] text-[#6A7282]";

const inputClass =
  "h-[46px] w-full rounded-[5px] border border-[#00000033] bg-white px-3 font-inter font-normal text-[14px] text-[black] outline-none transition focus:border-[#313131]";

const selectClass = `${inputClass} cursor-pointer appearance-auto`;

const sectionTitleClass =
  "font-montserrat text-[32px] font-bold font-black uppercase leading-none tracking-[-0.04em] text-black md:text-[36px]";

const sectionSubtitleClass =
  "mt-1 font-spaceMono text-[12px] font-normal uppercase text-[#6A7282]";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

function TextInput({ name, value, onChange, type = "text", disabled = false }) {
  return (
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
      className={`${inputClass} ${disabled ? "cursor-not-allowed bg-[#F3F3F3] text-[#777]" : ""}`}
    />
  );
}

function SelectInput({ name, value, onChange, children }) {
  return (
    <select
      name={name}
      value={value || ""}
      onChange={onChange}
      className={selectClass}
    >
      {children}
    </select>
  );
}

const NewAccountPage = () => {
  const { data: me, refetch: refetchProfile } = useProfileReadQuery();

  const { data: bodyTypes = [] } = useGetBodyTypesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const { data: faceShapes = [] } = useGetFaceShapesQuery();
  const { data: seasonalColors = [] } = useGetSeasonalColorsQuery();
  const [advancedMeasurementsModalOpen, setAdvancedMeasurementsModalOpen] = useState(false);

  const [updateAccount, { isLoading: isAccountSaving }] =
    useProfileUpdateUpdateMutation();

  const [updateMeasurements, { isLoading: isMeasurementsSaving }] =
    useProfileMeasurementsUpdateUpdateMutation();

  const [deleteMyAccount] = useDeleteDeactivateAccountMutation();

  const [unit, setUnit] = useState("imperial");
  const [selectedSize, setSelectedSize] = useState("XS");
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const [measurementData, setMeasurementData] = useState({
    feet: "",
    inches: "",
    height: "",
    weight: "",
    face_shape: "",
    body_type: "",
    seasonal_color: "",
    waist_size: "",
    inseam: "",
    hair_color: "",
    eye_color: "",
    bra_band_size: "",
    bra_cup_size: "",
    complexion: "",
    skin_tone: "",
    gender: "",
  });

  const [accountData, setAccountData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    old_password: "",
    new_password: "",
  });

  const [authTick, setAuthTick] = useState(0);
  const authToken = useMemo(() => {
    return localStorage.getItem("authToken");
  }, [authTick]);
  const isAuthenticated = Boolean(authToken);  

  const isMale = (() => {
    const gender = me?.gender;
    if (gender === null || gender === undefined) return false;
    return String(gender).toLowerCase() === "male";
  })();

  useEffect(() => {
    if (!me) return;

    const heightParts = me.height ? me.height.toString().split(".") : ["", ""];

    setAccountData({
      first_name: me.first_name || "",
      last_name: me.last_name || "",
      email: me.email || "",
      phone_number: me.phone_number || "",
      address_line1: me.address_line1 || "",
      address_line2: me.address_line2 || "",
      city: me.city || "",
      state: me.state || "",
      country: me.country || "",
      postal_code: me.postal_code || "",
      old_password: "",
      new_password: "",
    });

    setMeasurementData({
      feet: heightParts[0] || "",
      inches: heightParts[1] || "",
      height: me.height || "",
      weight: me.weight || "",
      waist_size: me.waist_size || "",
      inseam: me.inseam || "",
      face_shape:
        faceShapes.find(
          (item) =>
            item.name?.toLowerCase().replace(/[\s_]+/g, "") ===
            me.face_shape?.toLowerCase().replace(/[\s_]+/g, ""),
        )?.id || "",
      body_type:
        bodyTypes.find(
          (item) => item.name?.toLowerCase() === me.body_type?.toLowerCase(),
        )?.id || "",
      seasonal_color:
        seasonalColors.find(
          (item) =>
            item.name?.toLowerCase() === me.seasonal_color?.toLowerCase(),
        )?.id || "",
      hair_color: me.hair_color || "",
      eye_color: me.eye_color || "",
      bra_band_size: me.bra_band_size || "",
      bra_cup_size: me.bra_cup_size?.toUpperCase() || "",
      complexion: me.complexion || "",
      skin_tone: me.skin_tone || "",
      gender: me.gender || "",
    });

    setSelectedSize(me.clothing_size || "XS");
    setUnit(me.measurement_system || "imperial");
  }, [me, bodyTypes, faceShapes, seasonalColors]);

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMeasurementChange = (e) => {
    const { name, value } = e.target;
    setMeasurementData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleHeightChange = (type, value) => {
    const numericValue = value.replace(/\D/g, "");

    setMeasurementData((prev) => {
      const next = {
        ...prev,
        [type]: numericValue,
      };

      if (next.feet && next.inches) {
        next.height = `${next.feet}.${next.inches}`;
      } else if (next.feet && !next.inches) {
        next.height = `${next.feet}.0`;
      } else {
        next.height = "";
      }

      return next;
    });
  };

  const handleImperialToggle = () => {
    if (unit === "metric") {
      convertMetricToImperial(measurementData, setMeasurementData, [
        { type: "height" },
        { type: "weight" },
        { type: "waist" },
        { type: "inseam" },
      ]);
    }

    setUnit("imperial");
  };

  const handleMetricToggle = () => {
    if (unit === "imperial") {
      convertImperialToMetric(measurementData, setMeasurementData, [
        { type: "height" },
        { type: "weight" },
        { type: "waist" },
        { type: "inseam" },
      ]);
    }

    setUnit("metric");
  };

  const handleSaveMeasurements = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        measurement_system: unit,
        clothing_size: selectedSize,
        height: emptyToNull(measurementData.height),
        weight: emptyToNull(measurementData.weight),
        face_shape: emptyToNull(measurementData.face_shape),
        body_type: emptyToNull(measurementData.body_type),
        seasonal_color: emptyToNull(measurementData.seasonal_color),
        waist_size: emptyToNull(measurementData.waist_size),
        inseam: emptyToNull(measurementData.inseam),
        hair_color: emptyToNull(measurementData.hair_color),
        eye_color: emptyToNull(measurementData.eye_color),
        complexion: emptyToNull(measurementData.complexion),
        skin_tone: emptyToNull(measurementData.skin_tone),
        ...(!isMale && {
          bra_band_size: emptyToNull(measurementData.bra_band_size),
          bra_cup_size: emptyToNull(measurementData.bra_cup_size),
        }),
      };

      await updateMeasurements({
        profileMeasurementUpdate: payload,
      }).unwrap();

      toast.success("Physical metrics updated successfully");
      await refetchProfile();
    } catch (error) {
      toast.error("Failed to update physical metrics");
      console.error(error);
    }
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();

    try {
      await updateAccount({
        profileUpdate: {
          first_name: accountData.first_name,
          last_name: accountData.last_name,
          email: accountData.email,
          phone_number: emptyToNull(accountData.phone_number),
          address_line1: emptyToNull(accountData.address_line1),
          address_line2: emptyToNull(accountData.address_line2),
          city: emptyToNull(accountData.city),
          state: emptyToNull(accountData.state),
          country: emptyToNull(accountData.country),
          postal_code: emptyToNull(accountData.postal_code),
          old_password: emptyToNull(accountData.old_password),
          new_password: emptyToNull(accountData.new_password),
        },
      }).unwrap();

      toast.success("Account updated successfully");
      setAccountData((prev) => ({
        ...prev,
        old_password: "",
        new_password: "",
      }));
      await refetchProfile();
    } catch (error) {
      toast.error("Failed to update account");
      console.error(error);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteMyAccount().unwrap();
      toast.success("Account deactivated successfully.");
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      window.location.href = "/";
    } catch (error) {
      toast.error("Something went wrong.");
      console.error(error);
    } finally {
      setIsConfirmModalOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F4] text-[#111]">
      <SEO
        title="Account Profile | Manage Your Eveness Account"
        description="Manage your Eveness account details, physical metrics, measurements, style preferences, password, and personalization settings."
        path="/account"
      />
      <NewNavbar bottomNav="account" />

      <main className="mx-auto w-full max-w-[1512px] px-4 pb-14 pt-10 md:px-10 md:pt-12">
        <form onSubmit={handleSaveMeasurements}>
          <section>
            <div className="flex flex-col gap-4 border-[#CFCFCF] pb-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h1 className={sectionTitleClass}>PHYSICAL_METRICS</h1>
                <p className={sectionSubtitleClass}>
                  &gt; STRUCTURAL PARAMETERS FOR ALGORITHM ALIGNMENT.
                </p>
              </div>

              <div className="flex h-[43px] w-fit items-center rounded-[5px] border border-[#00000033] bg-[#0000000D] px-1 py-1">
                <button
                  type="button"
                  onClick={handleImperialToggle}
                  className={`h-[33px] px-4 py-2 font-spaceMono text-[10px] font-bold uppercase tracking-[0.18em] ${
                    unit === "imperial"
                      ? "bg-white text-black shadow-[0px_1px_2px_-1px_#0000001A,0px_1px_3px_0px_#0000001A] border-t-[1px] border-t-[#0000001A] rounded-[5px]"
                      : "text-[#6A7282]"
                  }`}
                >
                  Imperial
                </button>
                <button
                  type="button"
                  onClick={handleMetricToggle}
                  className={`h-[33px] px-4 py-2 font-spaceMono text-[10px] font-bold uppercase tracking-[0.18em] ${
                    unit === "metric"
                      ? "bg-white text-black shadow-[0px_1px_2px_-1px_#0000001A,0px_1px_3px_0px_#0000001A] border-t-[1px] border-t-[#0000001A] rounded-[5px]"
                      : "text-[#6A7282]"
                  }`}
                >
                  Metric
                </button>
              </div>
            </div>

            <div className="mb-6 rounded-[5px] border border-[#0000001A] bg-[#0000000D] p-4 flex flex-row gap-[25px] md:gap-[40px] items-center">
              <label className="block font-spaceMono text-[10px] font-bold uppercase tracking-[2px] text-[#6A7282]">Size</label>
              <div className="flex gap-2">
                {sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setSelectedSize(size)}
                    className={`md:h-[48px] md:min-w-[48px] h-[38px] min-w-[38px] rounded-[5px] border px-3 border-[#00000033] font-spaceMono text-[10px] font-bold ${
                      selectedSize === size
                        ? "border-black bg-black text-white"
                        : "border-[#00000033] bg-white text-black"
                    }`} 
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Height">
                {unit === "imperial" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <TextInput
                      name="feet"
                      value={measurementData.feet}
                      onChange={(e) =>
                        handleHeightChange("feet", e.target.value)
                      }
                    />
                    <TextInput
                      name="inches"
                      value={measurementData.inches}
                      onChange={(e) =>
                        handleHeightChange("inches", e.target.value)
                      }
                    />
                  </div>
                ) : (
                  <TextInput
                    name="height"
                    value={measurementData.height}
                    onChange={handleMeasurementChange}
                  />
                )}
              </Field>

              <Field label="Weight">
                <TextInput
                  name="weight"
                  value={measurementData.weight}
                  onChange={handleMeasurementChange}
                />
              </Field>

              <Field label="Face Shape">
                <SelectInput
                  name="face_shape"
                  value={measurementData.face_shape}
                  onChange={handleMeasurementChange}
                >
                  <option value="">Select</option>
                  {faceShapes.map((shape) => (
                    <option key={shape.id} value={shape.id}>
                      {shape.name}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              <Field label="Body Shape">
                <SelectInput
                  name="body_type"
                  value={measurementData.body_type}
                  onChange={handleMeasurementChange}
                >
                  <option value="">Select</option>
                  {bodyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              <Field label="Color Analysis">
                <SelectInput
                  name="seasonal_color"
                  value={measurementData.seasonal_color}
                  onChange={handleMeasurementChange}
                >
                  <option value="">Select</option>
                  {seasonalColors.map((color) => (
                    <option key={color.id} value={color.id}>
                      {color.name}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              <Field label="Waist Size">
                <TextInput
                  name="waist_size"
                  value={measurementData.waist_size}
                  onChange={handleMeasurementChange}
                />
              </Field>

              <Field label="Inseam">
                <TextInput
                  name="inseam"
                  value={measurementData.inseam}
                  onChange={handleMeasurementChange}
                />
              </Field>

              <Field label="Hair Color">
                <SelectInput
                  name="hair_color"
                  value={measurementData.hair_color}
                  onChange={handleMeasurementChange}
                >
                  <option value="">Select</option>
                  {hairColors.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              <Field label="Eye Color">
                <SelectInput
                  name="eye_color"
                  value={measurementData.eye_color}
                  onChange={handleMeasurementChange}
                >
                  <option value="">Select</option>
                  {eyeColors.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              <Field label="Complexion">
                <SelectInput
                  name="complexion"
                  value={measurementData.complexion}
                  onChange={handleMeasurementChange}
                >
                  <option value="">Select</option>
                  {complexionTones.map((tone) => (
                    <option key={tone} value={tone}>
                      {tone}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              <Field label="Skin Tone">
                <SelectInput
                  name="skin_tone"
                  value={measurementData.skin_tone}
                  onChange={handleMeasurementChange}
                >
                  <option value="">Select</option>
                  {skinTones.map((tone) => (
                    <option key={tone} value={tone}>
                      {tone}
                    </option>
                  ))}
                </SelectInput>
              </Field>

              {!isMale && (
                <>
                  <Field label="Bra Band Size">
                    <SelectInput
                      name="bra_band_size"
                      value={measurementData.bra_band_size}
                      onChange={handleMeasurementChange}
                    >
                      <option value="">Select</option>
                      {braBandSizes.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </SelectInput>
                  </Field>

                  <Field label="Bra Cup Size">
                    <SelectInput
                      name="bra_cup_size"
                      value={measurementData.bra_cup_size}
                      onChange={handleMeasurementChange}
                    >
                      <option value="">Select</option>
                      {braCupSizes.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </SelectInput>
                  </Field>
                </>
              )}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:max-w-[900px]">
              <button
                type="submit"
                disabled={isMeasurementsSaving}
                className="h-[42px] rounded-[5px] bg-[#252525] font-inter font-normal text-[14px] text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isMeasurementsSaving ? "Saving..." : "Save Data"}
              </button>

              <button
                type="button"
                onClick={() => setSurveyModalOpen(true)}
                className="h-[42px] rounded-[5px] bg-[#252525] font-inter font-normal text-[14px] text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
              >
                Restart Survey
              </button>
            </div>
          </section>
        </form>

        <form onSubmit={handleSaveAccount}>
          <section className="mt-24">
            <div className="mb-8 border-b border-[#CFCFCF] pb-4">
              <h2 className={sectionTitleClass}>USER_DATA</h2>
              <p className={sectionSubtitleClass}>
                &gt; IDENTIFICATION AND ROUTING PARAMETERS.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
              <Field label="First Name">
                <TextInput
                  name="first_name"
                  value={accountData.first_name}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="Last Name">
                <TextInput
                  name="last_name"
                  value={accountData.last_name}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="Email Address" className="md:col-span-2">
                <TextInput
                  name="email"
                  type="email"
                  value={accountData.email}
                  onChange={handleAccountChange}
                  disabled
                />
              </Field>

              <Field label="Phone Number" className="md:col-span-2">
                <TextInput
                  name="phone_number"
                  value={accountData.phone_number}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="Address Line 1">
                <TextInput
                  name="address_line1"
                  value={accountData.address_line1}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="Address Line 2">
                <TextInput
                  name="address_line2"
                  value={accountData.address_line2}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="City">
                <TextInput
                  name="city"
                  value={accountData.city}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="State">
                <TextInput
                  name="state"
                  value={accountData.state}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="Country">
                <TextInput
                  name="country"
                  value={accountData.country}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="Postal Code">
                <TextInput
                  name="postal_code"
                  value={accountData.postal_code}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="Old Password">
                <TextInput
                  type="password"
                  name="old_password"
                  value={accountData.old_password}
                  onChange={handleAccountChange}
                />
              </Field>

              <Field label="New Password">
                <TextInput
                  type="password"
                  name="new_password"
                  value={accountData.new_password}
                  onChange={handleAccountChange}
                />
              </Field>
            </div>

            <div className="mt-7 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(true)}
                className="w-fit font-inter font-bold text-[12px] underline"
              >
                Delete your account
              </button>

              <button
                type="submit"
                disabled={isAccountSaving}
                className="h-[42px] w-full rounded-[5px] bg-[#252525] font-inter font-normal text-[14px] text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 md:w-[260px]"
              >
                {isAccountSaving ? "Saving..." : "Save Account"}
              </button>
            </div>
          </section>
        </form>
      </main>

      <Footer />

      <HomeProfilePopupFlow
        isOpen={surveyModalOpen}
        mode="restart"
        onClose={() => setSurveyModalOpen(false)}
        onRestartComplete={async () => {
          await refetchProfile();
          setSurveyModalOpen(false);
          setAdvancedMeasurementsModalOpen(true);
        }}
        isAuthenticated={isAuthenticated}
      />

      <AdvancedMeasurementsProModal
        isOpen={advancedMeasurementsModalOpen}
        onClose={() => setAdvancedMeasurementsModalOpen(false)}
        isResetMode={true}
      />
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold">
              Are you sure you wish to delete your account?
            </h2>
            <p className="mb-6 text-sm text-gray-600">
              All of your progress will be lost. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                className="rounded bg-gray-200 px-4 py-2 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewAccountPage;
